import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import RouteSuspenseFallback from "../components/LoaderOverlay/RouteSuspenseFallback";
import SignIn from "../Auth/pages/SignIn";
import NotificationGrid from "../components/Navbar/AppNotification/NotificationGrid";
import AccessDenied from "../Pages/AccessDenied/AccessDenied";
import { useAppSelector } from "../Redux/hooks";
import { canAccessTaxReport, checkPermission } from "../utils/permissionUtils";
import { getSellerOrgBuildConfig } from "../config/sellerOrgConfig";
import Registeration from "../Auth/Registeration/Registeration";
import { UserRegistrationLayout } from "../Pages/UserRegistration/UserRegistrationLayout";
import ChangePassword from "../Auth/ChangePassword/ChangePassword";
import OrderHistory from "../Pages/OrdersHistory/OrdersHistory";
import OrdersHistory from "../Pages/OrdersHistory/OrdersHistory";
import OrderHistoryTab from "../Pages/Orders/Tabs/OrderHistoryTab";
import DishManagement from "../Pages/BuyerConfiguration/DishManagement/DishManagement";
import PromoCodeManagement from "../Pages/BuyerConfiguration/PromoCodeManagement/PromoCodeManagement";
import CampaignDashboard from "../Pages/BuyerConfiguration/PromoCodeManagement/CampaignDashboard";
import CreateCampaign from "../Pages/BuyerConfiguration/PromoCodeManagement/CreateCampaign";
import ViewCampaign from "../Pages/BuyerConfiguration/PromoCodeManagement/ViewCampaign";
import ViewCoupon from "../Pages/BuyerConfiguration/PromoCodeManagement/ViewCoupon";
import CampaignAnalyticsDashboard from "../Pages/BuyerConfiguration/PromoCodeManagement/CampaignAnalyticsDashboard";
import EditCampaign from "../Pages/BuyerConfiguration/PromoCodeManagement/EditCampaign";
import PublicNotification from "../Pages/BuyerConfiguration/PublicNotification/PublicNotification";
import CollectionManagement from "../Pages/BuyerConfiguration/CollectionManagement/CollectionManagement";
import CollectionEntriesPage from "../Pages/BuyerConfiguration/CollectionManagement/CollectionEntriesPage";
// Lazy Imports
const ForgotPassword = lazy(() => import("../Auth/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("../Auth/pages/ResetPassword"));
const AppLayout = lazy(() => import("../Layout/AppLayout"));
const PageLayout = lazy(() => import("../Layout/PageLayout"));
const Dashboard = lazy(() => import("../Pages/Dashboard/Dashboard"));
const DashboardOrdersTabs = lazy(() => import("../Pages/Dashboard/components/DashboardOrders/DashboardOrdersTabs"));
const OrderListDashboard = lazy(() => import("../Pages/Dashboard/components/DashboardOrders/OrderListDashboard"));
const OrderHistoryDashboard = lazy(() => import("../Pages/Dashboard/components/DashboardOrders/OrderHistoryDashboard"));
const Profile = lazy(() => import("../Pages/Profile/Profile"));

// Category
const CategoryPage = lazy(() => import("../Pages/Category/CategoryPage"));
const SubCategory = lazy(() => import("../Pages/Category/SubCategory/SubCategory"));
const CustomFields = lazy(() => import("../Pages/CustomFields/CustomFields"));

// Attributes
const Attributes = lazy(() => import("../Pages/Attributes/Attributes"));
const AddAttributes = lazy(() => import("../Pages/Attributes/Fragments/AddAttributes"));

// Customization
const CustomizationCategories = lazy(() => import("../Pages/CustomizationCategories/CustomizationCategories"));

// Store
const Stores = lazy(() => import("../Pages/Stores/Stores"));
const AddStoreTabs = lazy(() => import("../Pages/Stores/AddStoreTabs/AddStoreTabs"));
const StoreStatusOverrideLogs = lazy(() => import("../Pages/Stores/StoreStatusOverrideLogs"));
const ServiceableArea = lazy(() => import("../Pages/ServiceableArea/ServiceableArea"));

// Banner Management
const BannerManagement = lazy(() => import("../Pages/BuyerConfiguration/BannerManagement/BannerManagement"));

// Products
const Products = lazy(() => import("../Pages/Products/Products"));
const ProductTabs = lazy(() => import("../Pages/Products/ProductTabs/ProductTabs"));
const PreDefinedProducts = lazy(() => import("../Pages/Products/Fragments/PreDefinedProducts/PreDefinedProducts"));

// Orders
const Shipment = lazy(() => import("../Pages/Shipment/Shipment"));
const InvoiceTabs = lazy(() => import("../Pages/Orders/Tabs/InvoiceTabs"));
const OrderDetails = lazy(() => import("../Pages/Orders/Fragments/OrderDetails/OrderDetails"));
const ViewInvoice = lazy(() => import("../Pages/Orders/Fragments/Invoice/ViewInvoice/ViewInvoice"));
const OrdersView = lazy(() => import("../Pages/Orders/Fragments/OrderInvoice/OrdersView"));

// Settlements

const SettlementTabs = lazy(() => import("../Pages/Settlements/Tabs/SettlementTabs"));
const StoreSettlements = lazy(() => import("../Pages/StoreSettlements/StoreSettlements"));

// Returns
const Return = lazy(() => import("../Pages/Return/Return"));
const ViewReturnInvoice = lazy(() => import("../Pages/Return/ViewReturnInvoice/ViewReturnInvoice"));

// Customers
const Customer = lazy(() => import("../Pages/Customer/Customer"));
const CustomerDetails = lazy(() => import("../Pages/Customer/Fragments/CustomerDetails"));

// Logs and Settings
const ONDCLogDashboard = lazy(() => import("../Pages/TransactionLog/ONDCLogDashboard"));
const OrganizationSettingsForm = lazy(() => import("../Pages/OrganizationSettings/Organization"));
const BuyerAppSettings = lazy(() => import("../Pages/AppSettings/AppSettings"));

// User & Role Management
const UserManagementPage = lazy(() => import("../Pages/UserManagement/UserManagement"));
const UserView = lazy(() => import("../Pages/UserManagement/Grid/UserView"));
const RoleManagement = lazy(() => import("../Pages/RoleManagement/RoleManagement"));
const RoleForm = lazy(() => import("../Pages/RoleManagement/Fragments/RoleForm/RoleForm"));

// Registration
const OwnerInformation = lazy(() => import("../Pages/UserRegistration/Fragments/OwnerInformation/OwnerInformation"));
const BusinessInformation = lazy(() => import("../Pages/UserRegistration/Fragments/BusinessDetails/BusinessDetails"));
const StoreInformation = lazy(() => import("../Pages/UserRegistration/Fragments/StoreInformation/StoreInformation"));
const BankDetails = lazy(() => import("../Pages/UserRegistration/Fragments/BankDetails/BankDetails"));
const RegistrationComplete = lazy(() => import("../Pages/UserRegistration/Fragments/CompletePage/RegistrationComplete"));



// Notification Settings
const NotificationSettings = lazy(() => import("../Pages/NotificationSettings/NotificationSettings"));

// Tax Management (Super Admin)
const TaxReport = lazy(() => import("../Pages/TaxManagement/TaxReport"));

// User Delete
const UserDelete = lazy(() => import("../Auth/UserDelete/UserDelete"));
const MsmeTerms = lazy(() => import("../Auth/pages/MsmeTerms"));
const MsmePrivacy = lazy(() => import("../Auth/pages/MsmePrivacy"));

const ProtectedRoutes = () => {
  const location = useLocation();
  const isAuthenticated = localStorage.getItem("LOGIN_STATUS") === "LOGIN";
  const isRegistered = JSON.parse(localStorage.getItem("userRegistration") || "null");
  const { isRegisteredState } = location.state || {};
  const permissions = useAppSelector((state) => state.roleAndPermission.permissions);
  const roleID = useAppSelector((state) => state.roleAndPermission.roleID);
  const serviceableAreaEnabled = getSellerOrgBuildConfig().features.serviceableAreaMenu;
  const analyticsEnabled = getSellerOrgBuildConfig().features.analyticsMenu;
  const masterMenuEnabled = getSellerOrgBuildConfig().features.masterMenu;
  
  // Helper to check if user is Super Admin
  const isSuperAdmin = () => roleID === 1;

  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
      <Routes>
        {isAuthenticated ? (
          <>
            <Route element={<AppLayout />}>
              {checkPermission(permissions, "dashboard", "view") && <Route path="/dashboard" element={<Dashboard />} />}
              {checkPermission(permissions, "dashboard", "view") && <Route path="/dashboard/orders" element={<DashboardOrdersTabs />} />}
              {checkPermission(permissions, "orderListDashboard", "view") && <Route path="/order-list-dashboard" element={<OrderListDashboard />} />}
              {checkPermission(permissions, "orderHistoryDashboard", "view") && <Route path="/order-history-dashboard" element={<OrderHistoryDashboard />} />}
              <Route path="/profile" element={<Profile />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/notifications/:store_id" element={<NotificationGrid />} />
              {serviceableAreaEnabled && checkPermission(permissions, "serviceableArea", "view") && (
                <Route path="/serviceable-area" element={<ServiceableArea />} />
              )}

              {/* Categories */}
              {masterMenuEnabled && checkPermission(permissions, "category", "view") && (
                <>
                  <Route path="/category" element={<CategoryPage />} />
                  <Route path="/category/childcategory" element={<SubCategory />} />
                  <Route path="/category/:categoryId/custom-fields" element={<CustomFields />} />
                  <Route path="/category/:categoryId/:subCategoryId/custom-fields" element={<CustomFields />} />
                </>
              )}

              {/* Attributes */}
              {masterMenuEnabled && checkPermission(permissions, "attributes", "view") && (
                <>
                  <Route path="/attributes" element={<Attributes />} />
                  <Route path="/attributes/create-attribute" element={<AddAttributes />} />
                  <Route path="/attributes/:id/update-attribute" element={<AddAttributes />} />
                </>
              )}

              {/* Customization */}
              {checkPermission(permissions, "customizationCategories", "view") && (
                <Route path="/customization-categories" element={<CustomizationCategories />} />
              )}

              {/* Stores */}
              <Route path="/stores" element={<Stores />} />
              <Route path="/stores/create-store" element={<AddStoreTabs />} />
              <Route path="/stores/edit-store/:store_id" element={<AddStoreTabs />} />
              <Route path="/stores/status-override-logs" element={<StoreStatusOverrideLogs />} />


              {/* Banner Management */}
              {checkPermission(permissions, "bannerManagement", "view") && (
                <Route path="/banner-management" element={<BannerManagement />} />
              )}

              {/* Dish Management */}
              {checkPermission(permissions, "dishManagement", "view") && (
                <Route path="/dish-management" element={<DishManagement />} />
              )}
              {/* Campaign Management */}
              {checkPermission(permissions, "promoCodeManagement", "view") && (
                <>
                  <Route path="/campaign-management" element={<CampaignDashboard />} />
                  <Route path="/campaign-management/list" element={<PromoCodeManagement />} />
                  <Route path="/campaign-management/create" element={<CreateCampaign />} />
                  <Route path="/campaign-management/:campaignId/edit" element={<EditCampaign />} />
                  <Route path="/campaign-management/:campaignId/analytics" element={<CampaignAnalyticsDashboard />} />
                  <Route path="/campaign-management/:campaignId/coupons/:couponId" element={<ViewCoupon />} />
                  <Route path="/campaign-management/:campaignId" element={<ViewCampaign />} />
                </>
              )}
              {/* Public Notification */}
              {checkPermission(permissions, "buyerPublicNotification", "view") && (
                <Route path="/buyer-public-notification" element={<PublicNotification />} />
              )}
              {/* Collection Management */}
              {checkPermission(permissions, "collectionManagement", "view") && (
                <>
                  <Route path="/collection-management" element={<CollectionManagement />} />
                  <Route
                    path="/collection-management/:collectionId/entries"
                    element={<CollectionEntriesPage />}
                  />
                </>
              )}
              {/* Customers */}
              {checkPermission(permissions, "customers", "view") && (
                <>
                  <Route path="/customers" element={<Customer />} />
                  <Route path="/customers/shipping-address" element={<CustomerDetails />} />
                  <Route path="/customers/:customerId/shipping-address" element={<CustomerDetails />} />
                </>
              )}

              {/* Logs & Settings */}
              {analyticsEnabled && checkPermission(permissions, "ondcTransactionLogs", "view") && (
                <Route path="/ONDC-Transactions-log" element={<ONDCLogDashboard />} />
              )}
              {checkPermission(permissions, "organization", "view") && (
                <Route path="/Organization" element={<OrganizationSettingsForm />} />
              )}
              {checkPermission(permissions, "buyerAppSettings", "view") && (
                <Route path="/buyer-app-settings" element={<BuyerAppSettings />} />
              )}

              {/* User Management */}
              {checkPermission(permissions, "users", "view") && <Route path="/Users" element={<UserManagementPage />} />}
              {checkPermission(permissions, "users", "view") && <Route path="/Users/:id/view" element={<UserView />} />}

              {/* Role Management */}
              {checkPermission(permissions, "roles", "view") && (
                <>
                  <Route path="/roles" element={<RoleManagement />} />
                  <Route path="/roles/create-role" element={<RoleForm />} />
                  <Route path="/roles/:role_id/update-role" element={<RoleForm />} />
                </>
              )}

              {/* Multi Store Settlements - Super Admin Only */}
              {isSuperAdmin() && (
                <Route path="/store-settlements" element={<StoreSettlements />} />
              )}

              {/* Notification Settings - Super Admin Only */}
              {isSuperAdmin() && (
                <Route path="/notification-settings" element={<NotificationSettings />} />
              )}

              {/* Tax Report: Super Admin + taxReport permission (see Role Management) */}
              {canAccessTaxReport(permissions, roleID) && (
                <Route path="/tax-management/tax-report" element={<TaxReport />} />
              )}

              {/* Catch-all */}
              <Route path="*" element={<AccessDenied />} />
            </Route>

            {/* Public routes accessible even when authenticated */}
            <Route path="/user-delete" element={<UserDelete />} />
            <Route path="/terms" element={<MsmeTerms />} />
            <Route path="/privacy" element={<MsmePrivacy />} />

            {/* Store Submodule */}
            <Route path="/stores" element={<PageLayout />}>
              {checkPermission(permissions, "storeDetails", "view") && (<>
                <Route path=":store_id/store-details" element={<AddStoreTabs />} />
              </>)}
              {checkPermission(permissions, "products", "view") && (<>
                <Route path=":store_id/products" element={<Products />} />
                <Route path=":store_id/products/create-product" element={<ProductTabs />} />
                <Route path=":store_id/products/import-popular-products" element={<PreDefinedProducts />} />
                <Route path=":store_id/products/update-product/:product_id" element={<ProductTabs />} />
              </>)}
              {checkPermission(permissions, "orders", "view") && (<>
                <Route path=":store_id/orders" element={<InvoiceTabs />} />
                <Route path=":store_id/orders/:id" element={<OrderDetails />} />
                <Route path=":store_id/orders-view/:id" element={<OrdersView />} />
                <Route path=":store_id/orders/:id/cancel-order" element={<OrderDetails />} />
                <Route path=":store_id/orders/:id/bill-order" element={<OrderDetails />} />
                <Route path=":store_id/invoice/:invoice_id" element={<ViewInvoice />} />
                <Route path=":store_id/return-invoice/:invoice_id" element={<ViewReturnInvoice />} />
                <Route path="returns" element={<Return />} />
                <Route path="shipment" element={<Shipment />} />
              </>)}
              
              {checkPermission(permissions, "ordersHistory", "view") && (<>
                <Route path=":store_id/orders-history" element={<OrderHistoryTab />} />
              </>)}
              
                {checkPermission(permissions,"settlements","view") && <>
                  <Route path=":store_id/settlements" element={<SettlementTabs/>}/>
                </>}
            </Route>
          </>
        ) : (
          <>
            {(isRegisteredState || (isRegistered && Object.keys(isRegistered).length > 0)) && (
              <Route path="/register" element={<UserRegistrationLayout />}>
                <Route index element={<OwnerInformation />} />
                <Route path="businessDetails" element={<BusinessInformation />} />
                <Route path="storeInformation" element={<StoreInformation />} />
                <Route path="bankKYC" element={<BankDetails />} />
                <Route path="successfull" element={<RegistrationComplete />} />
              </Route>
            )}
            <Route path="/" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/registration" element={<Registeration />} />
            <Route path="/user-delete" element={<UserDelete />} />
            <Route path="/terms" element={<MsmeTerms />} />
            <Route path="/privacy" element={<MsmePrivacy />} />
            <Route path="*" element={<Navigate to="/" />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
};

export default ProtectedRoutes;
